import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import { Form, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import DocumentDetailsDialog from '@/entity/components/content/components/document-details-dialog';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import useDocumentField from '@/entity/hooks/use-document-field';
import useDocumentText from '@/entity/hooks/use-document-text';
import type { TransformedDocument } from '@/entity/hooks/use-documents';
import useDocumentsQueryParams from '@/entity/hooks/use-documents-query-params';
import DocumentViewerContainer from './components/document-viewer-container';
import ItemLabel from './components/item-label';
import ItemValue from './components/item-value';
export type DocumentItemProps = {
  document: TransformedDocument;
  entity: Entity;
  value: string;
  showOnboardingLink?: boolean;
  /**
   * For a second listing of a document already on the page: no viewer (it opens from the URL, so
   * two would stack) and no decrypt checkbox (both would register in the page's one decrypt form).
   */
  readOnly?: boolean;
  seqno?: number;
};
const DocumentItem = t0 => {
  const $ = _c(43);
  const {
    document,
    entity,
    value,
    showOnboardingLink: t1,
    readOnly: t2,
    seqno
  } = t0;
  const showOnboardingLink = t1 === undefined ? true : t1;
  const readOnly = t2 === undefined ? false : t2;
  const {
    t
  } = useTranslation("entity-details");
  let t3;
  if ($[0] !== entity.data || $[1] !== entity.id || $[2] !== seqno) {
    t3 = {
      entityAttributes: entity.data,
      entityId: entity.id,
      seqno
    };
    $[0] = entity.data;
    $[1] = entity.id;
    $[2] = seqno;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const vault = useEntityVault(t3);
  const form = useDecryptForm();
  let t4;
  if ($[4] !== entity || $[5] !== seqno) {
    t4 = {
      entity,
      seqno
    };
    $[4] = entity;
    $[5] = seqno;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    getProps,
    getRelatedDisByFormValue
  } = useDocumentField(t4);
  const field = vault.data ? getProps(document) : {
    isDecrypting: false,
    isDecryptable: false,
    isDecrypted: false,
    canSelect: false,
    showCheckbox: false,
    disabled: true
  };
  let t5;
  if ($[7] !== document || $[8] !== getRelatedDisByFormValue || $[9] !== vault.data) {
    t5 = vault.data ? getRelatedDisByFormValue(document) : [];
    $[7] = document;
    $[8] = getRelatedDisByFormValue;
    $[9] = vault.data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const dis = t5;
  const {
    values
  } = useDocumentsQueryParams();
  const open = !readOnly && values.documentId === document.id;
  const showCheckbox = !readOnly && field.showCheckbox;
  const getDocText = useDocumentText();
  let t6;
  if ($[11] !== document.kind || $[12] !== document.uploads || $[13] !== getDocText) {
    t6 = document.kind === "custom" ? document.uploads[0]?.identifier : getDocText(document.kind);
    $[11] = document.kind;
    $[12] = document.uploads;
    $[13] = getDocText;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const title = t6;
  const isCustomDocument = document.kind === "custom";
  let t7;
  if ($[15] !== dis || $[16] !== form) {
    t7 = event => {
      if (event.target.checked) {
        form.setFields(dis);
      } else {
        form.unsetFields(dis);
      }
    };
    $[15] = dis;
    $[16] = form;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleChange = t7;
  const t8 = "flex justify-between items-center gap-2 max-w-full group md:gap-0";
  const t9 = !readOnly && <div className={cx("block min-w-0 md:min-w-auto", {
    hidden: !field.showCheckbox
  })}><Tooltip disabled={field.canSelect} content={field.isDecrypted ? t(_temp, {
      field: "Document"
    }) : t(_temp2)}><Form.Checkbox name="documentKinds" disabled={field.disabled} label={<ItemLabel document={document} title={title} />} value={value} onChange={handleChange} /></Tooltip><div className="hidden">{dis.map(di => <input type="checkbox" disabled={field.disabled} key={`hidden-${di}`} {...form.register("fields")} value={di} />)}</div></div>;
  const t10 = cx("flex min-w-0 md:min-w-auto", {
    hidden: showCheckbox
  });
  let t11;
  if ($[18] !== document || $[19] !== showOnboardingLink || $[20] !== title) {
    t11 = <ItemLabel document={document} title={title} showOnboardingLink={showOnboardingLink} />;
    $[18] = document;
    $[19] = showOnboardingLink;
    $[20] = title;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== document.id || $[26] !== field.isDecrypted || $[27] !== field.isDecrypting) {
    t13 = <ItemValue isDecrypted={field.isDecrypted} isDecrypting={field.isDecrypting} documentId={document.id} />;
    $[25] = document.id;
    $[26] = field.isDecrypted;
    $[27] = field.isDecrypting;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t13 || $[31] !== t9) {
    t14 = <div className={t8}>{t9}{t12}{t13}</div>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== document || $[34] !== entity || $[35] !== isCustomDocument || $[36] !== open || $[37] !== seqno || $[38] !== vault) {
    t15 = open && vault.data && (isCustomDocument ? <DocumentViewerContainer key={document.id} document={document} entity={entity} vault={vault} seqno={seqno} /> : <DocumentDetailsDialog key={document.id} document={document} entity={entity} vault={vault} seqno={seqno} />);
    $[33] = document;
    $[34] = entity;
    $[35] = isCustomDocument;
    $[36] = open;
    $[37] = seqno;
    $[38] = vault;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t14 || $[41] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
};
export default DocumentItem;
function _temp($) {
  return $.decrypt.fieldDecryptedNotAllowed;
}
function _temp2($_0) {
  return $_0.decrypt.notAllowed;
}